import { uniqBy } from 'ramda';

import type {
  ContainmentGrid,
  DomainGuid,
  GridElement,
  GridElementSection
} from '../..';
import type { GridElementPropertyAttentionStatusByCalculationType } from '../../evaluation/AttentionStatusRepository';
import { getLoadFlowDetailsGridElementsRequiringAttention } from '../../evaluation/loadFlow/attentionRequired';
import { getShortCircuitDetailsGridElementsRequiringAttention } from '../../evaluation/shortCircuit/attentionRequired';
import type { GridElementObjectTypes } from '../gridElementsDetailsByCalculationType/GridElementType';
import type { GridElementsDetailsByCalculationType } from '../gridElementsDetailsByCalculationType/GridElementsDetails';
import type { GridElementsLoadFlowDetails } from '../gridElementsDetailsByCalculationType/loadFlow/gridElementLoadFlowDetails.types';
import type { GridElementsShortCircuitDetails } from '../gridElementsDetailsByCalculationType/shortCircuit/ShortCircuitDetails';
import { getContainersWithGridElements } from './getContainersWithGridElements';

export function createEvaluatedGridElementsSummary(
  containment: ContainmentGrid,
  gridElementsDetails: GridElementsDetailsByCalculationType,
  statusesByType: GridElementPropertyAttentionStatusByCalculationType
): GridElementSection<GridElement>[] {
  const loadFlowResultsRequiringAttention =
    getLoadFlowDetailsGridElementsRequiringAttention(
      gridElementsDetails,
      statusesByType.loadFlow
    );
  const shortCircuitResultsRequiringAttention =
    getShortCircuitDetailsGridElementsRequiringAttention(
      gridElementsDetails,
      statusesByType.shortCircuit
    );

  const containersWithGridElements = getContainersWithGridElements(
    containment,
    loadFlowResultsRequiringAttention,
    shortCircuitResultsRequiringAttention
  );
  const aclineSection: GridElementSection<GridElement> =
    createAcLineSegmentSection(
      loadFlowResultsRequiringAttention,
      containersWithGridElements
    );
  const powerTransformerSections: GridElementSection<GridElement> =
    createPowerTransformerSection(
      loadFlowResultsRequiringAttention,
      containersWithGridElements
    );

  const energyConsumersSection: GridElementSection<GridElement> =
    createEnergyConsumerSection(
      shortCircuitResultsRequiringAttention,
      loadFlowResultsRequiringAttention
    );

  const busbarSection: GridElementSection<GridElement> = createBusbarSection(
    shortCircuitResultsRequiringAttention,
    loadFlowResultsRequiringAttention,
    containersWithGridElements
  );

  return mergeSections(
    ...containersWithGridElements,
    aclineSection,
    energyConsumersSection,
    powerTransformerSections,
    busbarSection
  );
}
function mergeSections(...sections: GridElementSection<GridElement>[]) {
  return sections
    .map(section => ({
      ...section,
      gridElements: uniqBy(
        gridElement => gridElement.objectId,
        section.gridElements
      )
    }))
    .filter(section => section.gridElements.length > 0);
}

function createAcLineSegmentSection(
  loadFlowResultsRequiringAttention: GridElementsLoadFlowDetails,
  containersWithGridElements: GridElementSection<GridElement>[]
): GridElementSection<GridElement> {
  return {
    header: 'ACLines',
    type: 'ACLines',
    gridElements: findGridElementsOutsideContainer(
      'AcLineSegment',
      loadFlowResultsRequiringAttention,
      containersWithGridElements
    )
  };
}

function createPowerTransformerSection(
  loadFlowResultsRequiringAttention: GridElementsLoadFlowDetails,
  containersWithGridElements: GridElementSection<GridElement>[]
): GridElementSection<GridElement> {
  return {
    header: 'PowerTransformers',
    type: 'PowerTransformer',
    gridElements: findGridElementsOutsideContainer(
      'PowerTransformer',
      loadFlowResultsRequiringAttention,
      containersWithGridElements
    )
  };
}

function createEnergyConsumerSection(
  shortCircuitResultsRequiringAttention: GridElementsShortCircuitDetails,
  loadFlowResultsRequiringAttention: GridElementsLoadFlowDetails
) {
  const evaluatedEnergyConsumersShortCircuit =
    shortCircuitResultsRequiringAttention
      .filter(
        evaluatedGridElement =>
          evaluatedGridElement.objectType === 'EnergyConsumer'
      )
      .map(evaluatedGridElement => ({
        label: evaluatedGridElement.label,
        title: evaluatedGridElement.title,
        objectId: evaluatedGridElement.id
      }));
  const energyConsumersSection: GridElementSection<GridElement> = {
    header: 'EnergyConsumers',
    type: 'EnergyConsumer',
    gridElements: loadFlowResultsRequiringAttention
      .filter(gridElement => gridElement.objectType === 'EnergyConsumer')
      .map(gridElement => ({
        label: gridElement.label,
        title: gridElement.title,
        objectId: gridElement.id
      }))
      .concat(evaluatedEnergyConsumersShortCircuit)
  };
  return energyConsumersSection;
}

function createBusbarSection(
  shortCircuitResultsRequiringAttention: GridElementsShortCircuitDetails,
  loadFlowResultsRequiringAttention: GridElementsLoadFlowDetails,
  containersWithGridElements: GridElementSection<GridElement>[]
) {
  const evaluatedBusbarsShortCircuit = shortCircuitResultsRequiringAttention
    .filter(
      evaluatedGridElement =>
        evaluatedGridElement.objectType === 'Busbar' &&
        notContainedWithin(containersWithGridElements, evaluatedGridElement)
    )
    .map(evaluatedGridElement => ({
      label: evaluatedGridElement.label,
      title: evaluatedGridElement.title,
      objectId: evaluatedGridElement.id
    }));

  const busbarsSection: GridElementSection<GridElement> = {
    header: 'Busbar',
    type: 'Busbar',
    gridElements: loadFlowResultsRequiringAttention
      .filter(
        gridElement =>
          gridElement.objectType === 'Busbar' &&
          notContainedWithin(containersWithGridElements, gridElement)
      )
      .map(gridElement => ({
        label: gridElement.label,
        title: gridElement.title,
        objectId: gridElement.id
      }))
      .concat(evaluatedBusbarsShortCircuit)
  };
  return busbarsSection;
}

function findGridElementsOutsideContainer(
  gridElementType: GridElementObjectTypes,
  evaluatedGridElementsRequiringAttention: GridElementsLoadFlowDetails,
  containersWithGridElements: GridElementSection<GridElement>[]
): GridElement[] {
  return evaluatedGridElementsRequiringAttention
    .filter(
      evaluatedGridElement =>
        evaluatedGridElement.objectType === gridElementType &&
        notContainedWithin(containersWithGridElements, evaluatedGridElement)
    )
    .map(evaluatedGridElement => {
      return {
        label: evaluatedGridElement.label,
        title: evaluatedGridElement.title,
        objectId: evaluatedGridElement.id
      };
    });
}
function notContainedWithin(
  containersWithGridElements: GridElementSection<GridElement>[],
  evaluatedGridElement: { id: DomainGuid }
): boolean {
  return !containersWithGridElements.some(container =>
    container.gridElements.some(
      gridElement => gridElement.objectId === evaluatedGridElement.id
    )
  );
}
